import React, { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import Leaderboard from "./Leaderboard";
import ScrollableSection from "./ScrollableSection";
import { CardContainer } from "./CardContainer";
import { SectionContainer } from "./SectionContainer";
import { Header } from "./Header";
import { clearSearch } from "../../features/store/appDataSlice";
const categories = [
  {
    key: "most_popular",
    title: "Most Popular Today",
    icon: "/png/MostPopular.png",
  },
  {
    key: "topup",
    title: "Direct Game Topup",
    icon: "/png/DirectGameTopUp.png",
  },
  {
    key: "game_cards",
    title: "Game Cards",
    icon: "/png/GameCards.png",
  },
  {
    key: "gift_cards",
    title: "Gift Cards",
    icon: "/png/GiftCards.png",
  },
  {
    key: "dg_coins",
    title: "DG Coins",
    icon: "/png/dg.png",
  },
  { key: "data", title: "Data Services", icon: "/png/PrepaidServices.png" },
  {
    key: "airtime",
    title: "Airtime Services",
    icon: "/png/PrepaidServices.png",
  },
  { key: "cable", title: "Cable TV", icon: "/png/CableTV.png" },
  {
    key: "electric",
    title: "Electric Payment",
    icon: "/png/ElectricityPayment.png",
  },
];

const Main = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { data: appData } = useAppSelector((state) => state.appData);
  const selectedCountry = useAppSelector((state) => state.locale.selectedCountry);
  
  const [isScrolled, setIsScrolled] = useState(false);

  // Filter providers based on selected country
const filterProvidersByCountry = useMemo(() => {
  if (!appData || !selectedCountry?.id) return appData;

  console.log('=== FILTERING START ===');
  console.log('Selected Country:', selectedCountry);

  const filteredData = { ...appData };

  const filterProviders = (categoryKey: string) => {
    const category = appData[categoryKey];
    if (!category?.providers) return category;

    console.log(`\n--- Filtering category: ${categoryKey} ---`);

    const filteredProviders = category.providers.filter((provider: any) => {
      console.log(`\nChecking provider: ${provider.name} (ID: ${provider.id})`);
      
      // Get categories for this provider
      const providerCategoryIds = category.categories  // ← Look in the SAME category object
        ?.filter((cat: any) => cat.provider_id === provider.id)
        ?.map((cat: any) => cat.id) || [];

      console.log('Provider category IDs:', providerCategoryIds);

      // IMPORTANT: Look for plans in the SAME category object, not in appData.data
      const plansToCheck = category.plans || [];  // ← Use category.plans instead of appData.data.plans
      
      console.log(`Plans available in ${categoryKey}:`, plansToCheck.length);

      const hasMatchingCountry = plansToCheck.some((plan: any) => {
        const isInCategory = providerCategoryIds.includes(plan.category_id);
        
        const hasCountry = plan.countries && Array.isArray(plan.countries) 
          ? plan.countries.includes(selectedCountry.id)
          : false;
        
        if (provider.name === 'DG Coins' || isInCategory) {
          console.log('Plan check:', {
            planName: plan.name,
            planId: plan.id,
            categoryId: plan.category_id,
            categoryMatch: isInCategory,
            planCountries: plan.countries,
            selectedCountryId: selectedCountry.id,
            hasCountry,
            bothMatch: isInCategory && hasCountry
          });
        }
        
        return isInCategory && hasCountry;
      });

      console.log(`Provider ${provider.name} has matching country: ${hasMatchingCountry}`);

      return hasMatchingCountry;
    });

    console.log(`Filtered providers for ${categoryKey}:`, filteredProviders.length);

    return {
      ...category,
      providers: filteredProviders,
    };
  };

  // Apply filtering to all categories
  categories.forEach(({ key }) => {
    filteredData[key] = filterProviders(key);
  });

  return filteredData;
}, [appData, selectedCountry?.id]);

  useEffect(() => {
    if (location.state?.scrollTo && !isScrolled) {
      const scrollToSection = () => {
        const sectionId = `section-${location.state.scrollTo}`;
        const sectionElement = document.getElementById(sectionId);

        if (sectionElement) {
          sectionElement.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          setIsScrolled(true);
          return true;
        }
        return false;
      };

      // Try immediately
      if (!scrollToSection()) {
        // If not found, try again after a short delay (components might still be rendering)
        const timer = setTimeout(() => {
          scrollToSection();
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [location.state, isScrolled, appData]); // Add appData to dependencies

  // Reset scroll state when location changes
  useEffect(() => {
    setIsScrolled(false);
  }, [location.state]);
  return (
    <>
      {/* Desktop View */}
      <div className="hidden bp:block bp:col-14 bp:pt-10 bp:pb-2 min-w-[300px]">
        {categories.map(({ key, title, icon }) => {
          const category = filterProvidersByCountry?.[key];
          const providers = category?.providers;
          return (
            providers && providers.length > 0 && (
              <ScrollableSection
                key={key}
                providers={providers}
                icon={icon}
                title={title}
                viewAllUrl={key}
              />
            )
          );
        })}
      </div>

      {/* Mobile View */}
      <div className="block bp:hidden col-6 px-4 pt-10 pb-2">
       {categories.map(({ key, title, icon }) => {
          const category = filterProvidersByCountry?.[key];
          const providers = category?.providers;
          return (
            providers && providers.length > 0 && (
              <SectionContainer key={key} id={`section-${key}`}>
                <Header icon={icon} title={title} viewAllUrl={key} />
                <CardContainer cardData={providers} url={key} />
              </SectionContainer>
            )
          );
        })}

        <Leaderboard />
      </div>
    </>
  );
};

export default Main;
